import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  BookOpen, 
  TrendingUp, 
  Shield, 
  CloudLightning, 
  FileText, 
  CheckCircle,
  ArrowRight,
  Phone,
  Download,
  BarChart3,
  Users,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

const ARTICLES = [
  {
    slug: "minnesota-storm-damage-statistics",
    title: "Minnesota Storm Damage Statistics Every Homeowner Should Know",
    excerpt: "38 billion-dollar severe storm events since 1980. Learn what these numbers mean for Minnesota homeowners and why storm damage awareness matters.",
    category: "Storm Statistics",
    icon: BarChart3,
    featured: true
  },
  {
    slug: "wind-damage-vs-hail-damage",
    title: "Wind Damage vs. Hail Damage: What's More Common In Minnesota?",
    excerpt: "Storm damage isn't always hail damage. Understand the differences between wind and hail damage and why both deserve attention.",
    category: "Storm Damage",
    icon: CloudLightning
  },
  {
    slug: "insurance-agents-recommend-roof-review",
    title: "Why Insurance Agents Recommend Homeowners Review Their Roof After Severe Storms",
    excerpt: "Insurance professionals value proactive homeowners. Learn why reviewing your roof after storms matters for your coverage.",
    category: "Insurance Insights",
    icon: Shield
  },
  {
    slug: "roof-replacement-costs-minnesota",
    title: "Roof Replacement Costs In Minnesota: Insurance Claim vs Paying Out Of Pocket",
    excerpt: "Understanding the true cost of roof replacement and how insurance claims can significantly reduce your out-of-pocket expenses.",
    category: "Roofing Costs",
    icon: FileText
  },
  {
    slug: "how-to-avoid-storm-chasers",
    title: "How To Avoid Storm Chasers After A Minnesota Storm",
    excerpt: "Not all contractors are trustworthy. Learn the red flags and questions that help you identify reputable professionals.",
    category: "Contractor Selection",
    icon: AlertTriangle
  },
  {
    slug: "true-cost-of-waiting-storm-damage",
    title: "The True Cost Of Waiting To Address Storm Damage",
    excerpt: "Delayed repairs can lead to escalating costs and insurance complications. Understand why timing matters.",
    category: "Storm Recovery",
    icon: TrendingUp
  },
  {
    slug: "why-wind-damage-is-often-missed",
    title: "Why Wind Damage Is Often Missed By Homeowners",
    excerpt: "Lifted shingles, creased materials, and hidden damage often go unnoticed. Learn what to look for after high winds.",
    category: "Storm Damage",
    icon: CloudLightning
  },
  {
    slug: "mistakes-homeowners-make-after-storm-damage",
    title: "Mistakes Minnesota Homeowners Make After Storm Damage",
    excerpt: "From waiting too long to choosing contractors too quickly, these common mistakes can cost homeowners significantly.",
    category: "Storm Recovery",
    icon: AlertTriangle
  },
  {
    slug: "is-minnesota-becoming-more-storm-prone",
    title: "Is Minnesota Becoming More Storm Prone?",
    excerpt: "Storm frequency and severity trends in Minnesota. What the data shows and what homeowners should know.",
    category: "Storm Statistics",
    icon: TrendingUp
  },
  {
    slug: "signs-your-roof-has-storm-damage",
    title: "Signs Your Roof May Have Storm Damage",
    excerpt: "Wind indicators, hail signs, gutter damage, and water infiltration. A comprehensive guide to identifying storm damage.",
    category: "Storm Damage",
    icon: CheckCircle
  }
];

const GUIDES = [
  {
    title: "Homeowner Storm Damage Checklist",
    description: "Step-by-step checklist for assessing your property after severe weather.",
    icon: CheckCircle
  },
  {
    title: "Insurance Claim Preparation Guide",
    description: "Everything you need to document and prepare before filing a storm damage claim.",
    icon: FileText
  },
  {
    title: "Questions To Ask A Contractor",
    description: "Essential questions to help you identify reputable roofing professionals.",
    icon: Users
  },
  {
    title: "Storm Damage Documentation Checklist",
    description: "How to properly document damage for insurance and contractor assessments.",
    icon: FileText
  },
  {
    title: "Roof Replacement Planning Guide",
    description: "What to expect and how to prepare when facing a roof replacement project.",
    icon: BookOpen
  },
  {
    title: "Understanding Insurance Deductibles",
    description: "How deductibles work and what they mean for your storm damage claim.",
    icon: Shield
  }
];

export default function ResourceCenter() {
  useSEO();

  const categories = [...new Set(ARTICLES.map(a => a.category))];
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredArticles = selectedCategory === "all" 
    ? ARTICLES 
    : ARTICLES.filter(a => a.category === selectedCategory);

  const featuredArticle = ARTICLES.find(a => a.featured);

  return (
    <div data-testid="resource-center-page" className="">
      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Minnesota Storm Insights & Resource Center",
            "description": "Educational resources, statistics, and guides for Minnesota homeowners dealing with storm damage.",
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": ARTICLES.map((article, idx) => ({
                "@type": "ListItem",
                "position": idx + 1,
                "item": {
                  "@type": "Article",
                  "name": article.title,
                  "description": article.excerpt
                }
              }))
            }
          })
        }}
      />

      {/* Hero Section */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <BookOpen className="w-4 h-4" />
              Knowledge Center
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6">
              Minnesota Storm Insights & Resource Center
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Educational resources, storm statistics, insurance insights, and guides to help 
              Minnesota homeowners make informed decisions after severe weather.
            </p>
          </div>
        </div>
      </section>

      {/* Statistics Highlight */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { id: "stat-1", value: "38", label: "Billion-dollar storm events in MN since 1980" },
              { id: "stat-2", value: "61%", label: "Of MN weather disasters are severe storms" },
              { id: "stat-3", value: "34%", label: "Of claims involve wind or hail damage" },
              { id: "stat-4", value: "$5.6B", label: "Paid in hail claims by State Farm (2025)" }
            ].map((stat) => (
              <div key={stat.id} className="bg-white border border-gray-200 rounded-xl p-5 text-center">
                <div className="text-3xl font-heading font-bold text-orange">{stat.value}</div>
                <p className="text-gray-600 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-semibold text-navy mb-6">Featured Article</h2>
            <Link 
              to={`/resources/${featuredArticle.slug}`}
              className="block bg-navy rounded-2xl p-8 md:p-12 hover:bg-navy-light transition-colors group"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-16 h-16 bg-orange/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <featuredArticle.icon className="w-8 h-8 text-orange" />
                </div>
                <div className="flex-1">
                  <span className="text-orange text-sm font-medium">{featuredArticle.category}</span>
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mt-1 mb-3 group-hover:text-orange transition-colors">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{featuredArticle.excerpt}</p>
                </div>
                <ArrowRight className="w-8 h-8 text-orange group-hover:translate-x-2 transition-transform" />
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Articles Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <h2 className="font-heading text-2xl font-semibold text-navy">Storm Damage Resources</h2>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === "all"
                    ? "bg-navy text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? "bg-navy text-white"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.filter(a => !a.featured).map((article, idx) => (
              <Link
                key={article.slug}
                to={`/resources/${article.slug}`}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-orange/30 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <article.icon className="w-5 h-5 text-orange" />
                  </div>
                  <span className="text-orange text-sm font-medium">{article.category}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2 group-hover:text-orange transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {article.excerpt}
                </p>
                <div className="flex items-center text-orange font-medium text-sm">
                  Read Article
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Minnesota Storm Recovery Guides
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Practical guides and checklists to help you navigate storm damage, insurance claims, 
              and contractor selection.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {GUIDES.map((guide) => (
              <div
                key={guide.title}
                className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:border-orange/30 transition-colors"
              >
                <div className="w-12 h-12 bg-navy/10 rounded-xl flex items-center justify-center mb-4">
                  <guide.icon className="w-6 h-6 text-navy" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                  {guide.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {guide.description}
                </p>
                <Link 
                  to={`/resources/guide/${guide.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="inline-flex items-center text-orange font-medium text-sm hover:text-orange-hover"
                >
                  <Download className="w-4 h-4 mr-1" />
                  View Guide
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Ready To Take The Next Step?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Whether you have questions about storm damage or need to connect with a 
            trusted contractor, we're here to help Minnesota homeowners make informed decisions.
          </p>
          <Link to="/#lead-form">
            <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-4 text-lg">
              <Phone className="w-5 h-5 mr-2" />
              Get Connected
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
